/**
 * getCurrentUIState.ts – Generic DOM introspection for AI UI awareness
 *
 * Reads `data-*` attributes from the DOM to build a structured, hierarchical
 * description of visible UI elements. The AI uses this to find, highlight,
 * and interact with UI elements.
 *
 * Supported attributes:
 *   data-group-key   – Groups / containers
 *   data-button-key  – Clickable buttons
 *   data-input-key   – Text inputs
 *   data-select-key  – Selection controls (radio groups, dropdowns)
 *   data-info-key    – Read-only labels / info elements
 *
 * Usage in your app: Simply add these attributes to your DOM elements:
 *   <button data-button-key="saveButton">Save</button>
 *   <div data-group-key="toolbar">...</div>
 */
import { roundValues } from "./roundValues";

export interface UIStateOptions {
  /** Include pixel positions and bounding rects in the output (default: false) */
  withPositionDetails?: boolean;
  /** Number of decimal places for rounding positions (default: 2) */
  precision?: number;
  /**
   * Optional callback to provide additional app-level context.
   * The returned object is merged into the root of the UI state.
   * Example: { page: "Editor", userName: "Max", lang: "de" }
   */
  getAppContext?: () => Record<string, any>;
}

export function getCurrentUIState(options: UIStateOptions = {}) {
  const { withPositionDetails = false, precision = 2, getAppContext } = options;

  const findParentsWithAttribute = (element: Element, attributeName: string) => {
    const parents: Element[] = [];
    let parent = element.parentElement;
    while (parent) {
      if (parent.hasAttribute(attributeName)) {
        parents.push(parent);
      }
      parent = parent.parentElement;
    }
    return parents.reverse();
  };

  const typeOfKey = (key: string) => key.split("-")[1];

  const createEntry = (element: Element, type: string, value: string) => {
    const { x, y, width, height } = element.getBoundingClientRect();
    return {
      [type]: value,
      ...(withPositionDetails ? { position: { x: x + width / 2, y: y + height / 2 } } : {}),
    };
  };

  const createGroup = (element: Element, type: string, value: string) => {
    const { x, y, width, height } = element.getBoundingClientRect();
    return {
      [type]: value,
      children: [] as any[],
      ...(withPositionDetails ? { rect: { x, y, width, height } } : {}),
    };
  };

  const root = createGroup(document.body, "group", "document");

  function isVisible(element: Element | null) {
    while (element) {
      const style = window.getComputedStyle(element);
      if (style.display === "none" || style.opacity === "0" || style.visibility === "hidden") {
        return false;
      }
      element = element.parentElement;
    }
    return true;
  }

  const registerElements = (root: any, elementKeys: [Element, string][]) => {
    let lastNode: any = null;
    for (const [element, key] of elementKeys) {
      if (!isVisible(element)) {
        break;
      }
      const value = element.getAttribute(key);
      const type = typeOfKey(key);
      let child = root.children.find((c: any) => c[type] == value);
      if (!child) {
        child = (type == "group" ? createGroup : createEntry)(element, type, value!);
        root.children.push(child);
      }
      lastNode = child;
      if (type !== "group") {
        break;
      }
      root = child;
    }
    return lastNode;
  };

  ["data-input-key", "data-button-key", "data-info-key", "data-select-key"].forEach((key) => {
    const elements = Array.from(document.querySelectorAll("*[" + key + "]"));
    elements.forEach((element) => {
      const parentElements = findParentsWithAttribute(element, "data-group-key");
      const node = registerElements(
        root,
        parentElements
          .map((parent) => [parent, "data-group-key"] as [Element, string])
          .concat([[element, key]])
      );
      if (node) {
        if (key == "data-select-key") {
          node.value = element.getAttribute("data-value");
          const radioInputs = Array.from(
            element.querySelectorAll<HTMLInputElement>('input[type="radio"]')
          );
          if (radioInputs.length > 0) {
            node.options = radioInputs.map((input) => input.value || input.id).filter(Boolean);
          }
        } else if (key == "data-input-key") {
          node.value = (element as any).value ?? element.querySelector("input")?.value;
        }
      }
    });
  });

  const appContext = getAppContext?.() ?? {};

  return roundValues(
    {
      ...appContext,
      uiElementsVisible: root.children,
    },
    precision
  );
}
